import { ScrollStory, StageCanvas, StageCard, StagePanels, type StoryBeat } from '../../../../components/ScrollStory';

/**
 * "How it works" for Domain Health — the real pipeline one domain travels inside a single
 * `DomainHealthService.getOrFetch()` call, DNS probes → composite 0–100 → badge → webhook.
 * Every step, field, weight and threshold is drawn from the domain-health code
 * (computeScore, SCORE_BAND_BOUNDARIES, DKIM_SELECTORS, MAX_TTL_SECONDS, detectAndFireTransitions).
 * The worked example follows `acme.co` — a well-configured sender that lands at 95 / excellent,
 * with SPF softened to ~all so the scoring stays legible.
 */

const STAGES = ['Enter', 'Probe DNS', 'Score', 'Band + badge', 'Cache', 'Alert'];

type Beat = StoryBeat & { detail: React.ReactNode };

const BEATS: Beat[] = [
  {
    n: 1,
    kicker: 'Enter',
    title: 'One domain, one lookup',
    body: (
      <>
        A refresh, an API call, or auto-warm the moment a new address is ingested — every path lands on one{' '}
        <span className="font-mono">getOrFetch</span>. If a snapshot under 24 hours old already exists we hand it back
        instantly: no DNS, no credit.
      </>
    ),
    facts: ['on demand · on ingest', 'one snapshot / domain', 'fresh < 24h → free'],
    detail: (
      <StageCard head="getOrFetch(acme.co)">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">recipient · sender · auto-warm</div>
          <div className="text-accent">→ one (tenant, domain) row</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          cache hit &lt; 24h · returns without a lookup
        </div>
      </StageCard>
    ),
  },
  {
    n: 2,
    kicker: 'Probe DNS',
    title: 'Four probes fire at once',
    body: (
      <>
        Against our own high-limit resolver we pull SPF (the <span className="font-mono">v=spf1</span> TXT + its{' '}
        <span className="font-mono">-all</span>/<span className="font-mono">~all</span> qualifier), DMARC, and MX. DKIM
        has no directory, so we blind-probe ~40 known selectors — <span className="font-mono">google</span>,{' '}
        <span className="font-mono">selector1</span>, <span className="font-mono">amazonses</span> — in batches of 6.
      </>
    ),
    facts: ['SPF · DMARC · MX', 'DKIM: ~40 selectors', 'batch of 6'],
    detail: (
      <StageCard head="Parallel DNS probes">
        <div className="grid grid-cols-1 gap-y-1 font-mono text-[11px] md:text-[12.5px]">
          <Row k="SPF" v="present · ~all" />
          <Row k="DKIM" v="google, selector1" ok />
          <Row k="DMARC" v="p=reject · pct=100" ok />
          <Row k="MX" v="10 mx.acme.co" />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          transient error → one retry after 150ms
        </div>
      </StageCard>
    ),
  },
  {
    n: 3,
    kicker: 'Score',
    title: 'Everything rolls into 0–100',
    body: (
      <>
        No MX means the domain can&rsquo;t receive mail — score 0, full stop. Otherwise SPF earns up to 30, DKIM up to 30
        (bonus for 2+ selectors), DMARC up to 40. Strict policies win: softening SPF to{' '}
        <span className="font-mono">~all</span> here leaves 5 points on the table.
      </>
    ),
    facts: ['no MX → 0', 'SPF 30 · DKIM 30 · DMARC 40', 'reject + -all → 100'],
    detail: (
      <StageCard head="computeScore">
        <div className="font-mono text-[11.5px] md:text-[12.5px]">
          <ScoreRow label="SPF present · ~all" pts="+25" />
          <ScoreRow label="DKIM · 2 selectors" pts="+30" />
          <ScoreRow label="DMARC · reject" pts="+40" />
          <div className="mt-1 flex items-center justify-between border-t border-bordersoft pt-1">
            <span className="font-semibold">score 95</span>
            <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-accent">
              excellent
            </span>
          </div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">-all instead of ~all would add +5</div>
      </StageCard>
    ),
  },
  {
    n: 4,
    kicker: 'Band + badge',
    title: 'A score becomes a signal',
    body: (
      <>
        The number maps to a band — 75+ excellent, 50–74 good, 25–49 mixed, under 25 poor — and lands as the
        traffic-light badge on every record and the domain detail page. Weak senders surface in a list the moment they
        show up, not weeks later.
      </>
    ),
    facts: ['75 · 50 · 25 bands', 'per-record badge', 'detail page'],
    detail: (
      <StageCard head="Score bands">
        <div className="space-y-1 font-mono text-[10.5px] md:text-[11.5px]">
          <BandRow label="excellent" range="75–100" active />
          <BandRow label="good" range="50–74" />
          <BandRow label="mixed" range="25–49" />
          <BandRow label="poor" range="0–24" />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">badge → /app/domains/health/acme.co</div>
      </StageCard>
    ),
  },
  {
    n: 5,
    kicker: 'Cache',
    title: 'Cached for 24 hours',
    body: (
      <>
        One snapshot per domain per workspace, keyed on <span className="font-mono">(tenant, domain)</span> — 10,000{' '}
        <span className="font-mono">@gmail.com</span> signups share a single lookup. Cost scales with unique domains,
        not row volume, until you force a rate-limited refresh.
      </>
    ),
    facts: ['(tenant, domain) key', '24h TTL', 'refresh 10/min'],
    detail: (
      <StageCard head="One row, many addresses">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">10,000 × alice@gmail.com …</div>
          <div className="text-accent">→ 1 cached gmail.com snapshot</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          expiresAt = now + 24h · force refresh is IP-throttled
        </div>
      </StageCard>
    ),
  },
  {
    n: 6,
    kicker: 'Alert',
    title: 'Regressions find you',
    body: (
      <>
        If a re-check drops a domain into a lower band, <span className="font-mono">domain_health.score_changed</span>{' '}
        fires; if it lands on a blacklist, <span className="font-mono">domain.blacklisted</span> fires. Both only on the
        transition, and score changes only downward — your integrations hear about problems, not noise.
      </>
    ),
    facts: ['transition-only', 'downward only', 'fire-and-forget'],
    detail: (
      <StageCard head="Webhook — score_changed">
        <pre className="overflow-x-auto font-mono text-[10.5px] leading-relaxed text-txt md:text-[12px]">
          <code>{`{ "event": "domain_health.score_changed",
  "domain": "acme.co",
  "previousScore": 95, "newScore": 48,
  "previousBand": "excellent", "newBand": "mixed" }`}</code>
        </pre>
      </StageCard>
    ),
  },
];

export function DomainHealthStory() {
  const panels = BEATS.map((b) => ({ n: b.n, content: b.detail }));
  return (
    <section className="mt-20" aria-label="How domain health works">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-accent">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" /> How it works
        </span>
        <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
          Inside one <span className="font-mono text-accent">getOrFetch</span> — DNS to badge
        </h2>
        <p className="mt-3 text-[14.5px] leading-relaxed text-muted">
          Follow <span className="font-mono">acme.co</span> through the real checker — four parallel DNS probes, a
          composite 0–100 score, and the per-record badge your team reads at a glance.
        </p>
      </div>

      <ScrollStory
        beats={BEATS}
        className="mt-6"
        renderStage={(beat, p) => (
          <StageCanvas>
            <div className="absolute inset-x-4 top-4 md:inset-x-6 md:top-6">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-faint md:text-[11px]">
                <span>Domain health pipeline</span>
                <span>step {beat}/6</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-border">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-accent to-allow transition-all duration-500"
                  style={{ width: `${(beat / 6) * 100}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-[12px] text-accent md:text-[13px]">{STAGES[beat - 1]}</div>
              <span className="sr-only">{`Progress ${Math.round(p * 100)}%`}</span>
            </div>
            <StagePanels beat={beat} panels={panels} />
          </StageCanvas>
        )}
      />
    </section>
  );
}

function Row({ k, v, ok }: { k: string; v: string; ok?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-faint">{k}</span>
      <span className={ok ? 'font-semibold text-allow' : 'text-txt'}>{v}</span>
    </div>
  );
}

function ScoreRow({ label, pts }: { label: string; pts: string }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-muted">{label}</span>
      <span className="text-accent">{pts}</span>
    </div>
  );
}

function BandRow({ label, range, active }: { label: string; range: string; active?: boolean }) {
  return (
    <div
      className={`flex items-center justify-between rounded border px-2 py-1 ${
        active ? 'border-accent/50 bg-accent/10 text-accent' : 'border-border text-muted'
      }`}
    >
      <span className="uppercase tracking-[0.06em]">{label}</span>
      <span>{range}</span>
    </div>
  );
}
